import type { ContainerTypeRow } from "../../api.js";
import { Alert, Button, Chip, faDigits, Field, Panel, Switch } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { distributionApi } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { askReason, ErrorLine, useAction } from "../common.js";
import { useOrgs } from "../menu/data.js";

const DIRECTION_LABELS: Record<string, { label: string; tone?: "ok" | "coral" | "warn" }> = {
  out: { label: "تحویل به واحد (خروج)", tone: "warn" },
  in: { label: "برگشت از واحد (ورود)", tone: "ok" },
  reversal: { label: "ابطال / اصلاح سند", tone: "coral" },
};

export function ContainersTab() {
  const orgs = useOrgs();
  const balances = useQuery({
    queryKey: ["distribution", "containers", "balances"],
    queryFn: () => distributionApi.listBalances(),
  });

  const types = useQuery({
    queryKey: ["distribution", "containers", "types"],
    queryFn: () => distributionApi.listContainerTypes(),
  });

  const [activeKardexTarget, setActiveKardexTarget] = useState<{
    unitId: string;
    containerTypeId: string;
    unitName: string;
    containerTypeName: string;
  } | null>(null);

  const [showTypesModal, setShowTypesModal] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);

  // Flatten unit balances for tabular view
  const flattenedBalances = (balances.data ?? []).flatMap((ub) =>
    ub.balances.map((b) => ({
      unitId: ub.unitId,
      unitName: ub.unitName,
      containerTypeId: b.containerTypeId,
      containerTypeCode: b.containerTypeCode,
      containerTypeName: b.containerTypeName,
      totalOut: b.totalOut,
      totalIn: b.totalIn,
      balance: b.balance,
      overdueBalance: b.overdueBalance,
      isOverdue: b.overdueBalance > 0,
    })),
  );

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Top action bar */}
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "center" }}>
        <h2 className="t-title">مدیریت و گردش ظروف امانی</h2>
        <span className="grow" />
        <Button
          variant="ghost"
          onClick={() => {
            setShowTypesModal(true);
          }}
        >
          تعریف انواع ظروف ({types.data?.length ?? 0})
        </Button>
        <Button
          variant="primary"
          icon="plus"
          onClick={() => {
            setShowManualEntryModal(true);
          }}
        >
          ثبت سند گردش دستی
        </Button>
      </div>

      {/* Overdue alert if any balance is overdue */}
      {flattenedBalances.some((b) => b.isOverdue) && (
        <Alert level="important" icon="alert" title="توجه: برخی واحدها دارای ظروف معوقه هستند">
          واحدهایی که بیش از مهلت مجاز ظرف امانی را بازنگردانده‌اند، با برچسب قرمز مشخص شده‌اند.
        </Alert>
      )}

      {/* Balances table */}
      <Panel title="مانده موجودی ظروف امانی نزد واحدها">
        {balances.isLoading && <p className="muted">در حال دریافت مانده‌ها...</p>}
        {balances.data && flattenedBalances.length === 0 && (
          <p className="ink2">در حال حاضر هیچ ظرف امانی نزد واحدها ثبت نشده است.</p>
        )}
        {balances.data && flattenedBalances.length > 0 && (
          <div className="card" style={{ padding: "var(--s2) var(--s4)" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>واحد سازمانی</th>
                  <th>نوع ظرف امانی</th>
                  <th>تعداد کل ارسالی</th>
                  <th>تعداد کل دریافتی</th>
                  <th>مانده امانت</th>
                  <th>وضعیت مهلت</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {flattenedBalances.map((b) => (
                  <tr
                    key={`${b.unitId}_${b.containerTypeId}`}
                    style={b.isOverdue ? { background: "var(--coral-soft)" } : undefined}
                  >
                    <td>
                      <div className="b">{b.unitName}</div>
                    </td>
                    <td className="m">{b.containerTypeName}</td>
                    <td className="num">{faDigits(b.totalOut)}</td>
                    <td className="num">{faDigits(b.totalIn)}</td>
                    <td className="num b" style={{ fontSize: 16 }}>
                      {faDigits(b.balance)} عدد
                    </td>
                    <td>
                      {b.isOverdue ? (
                        <Chip tone="coral">{faDigits(b.overdueBalance)} عدد مهلت سپری‌شده</Chip>
                      ) : b.balance > 0 ? (
                        <Chip plain>در مهلت مجاز</Chip>
                      ) : (
                        <Chip tone="ok">تسویه</Chip>
                      )}
                    </td>
                    <td className="end">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setActiveKardexTarget({
                            unitId: b.unitId,
                            containerTypeId: b.containerTypeId,
                            unitName: b.unitName,
                            containerTypeName: b.containerTypeName,
                          });
                        }}
                      >
                        گردش حساب (کاردکس)
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Kardex Modal */}
      {activeKardexTarget && (
        <KardexModal
          target={activeKardexTarget}
          onClose={() => {
            setActiveKardexTarget(null);
          }}
        />
      )}

      {/* Manual Entry Modal */}
      {showManualEntryModal && (
        <ManualEntryModal
          units={orgs.units}
          types={types.data ?? []}
          onClose={() => {
            setShowManualEntryModal(false);
          }}
        />
      )}

      {/* Container Types Modal */}
      {showTypesModal && (
        <ContainerTypesModal
          types={types.data ?? []}
          onClose={() => {
            setShowTypesModal(false);
          }}
        />
      )}
    </div>
  );
}

function KardexModal({
  target,
  onClose,
}: {
  target: { unitId: string; containerTypeId: string; unitName: string; containerTypeName: string };
  onClose: () => void;
}) {
  const kardex = useQuery({
    queryKey: ["distribution", "containers", "kardex", target.unitId, target.containerTypeId],
    queryFn: () => distributionApi.getKardex(target.unitId, target.containerTypeId),
  });

  const reverse = useAction(
    async ({ entryId, reason }: { entryId: string; reason: string }) => {
      await distributionApi.reverseContainerEntry(entryId, reason);
    },
    [
      ["distribution", "containers", "kardex", target.unitId, target.containerTypeId],
      ["distribution", "containers", "balances"],
    ],
  );

  return (
    <Panel
      title={`کاردکس ظرف «${target.containerTypeName}» — واحد «${target.unitName}»`}
      actions={
        <Button variant="ghost" onClick={onClose}>
          بستن
        </Button>
      }
    >
      <div className="stack" style={{ gap: "var(--s3)" }}>
        {kardex.isLoading && <p className="muted">در حال دریافت گردش حساب...</p>}
        {kardex.data && (
          <>
            <div className="card" style={{ padding: "var(--s3)", background: "var(--panel)" }}>
              <div
                className="grid"
                style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--s2)" }}
              >
                <div>
                  کل ارسالی: <strong>{faDigits(kardex.data.totalOut)} عدد</strong>
                </div>
                <div>
                  کل برگشتی: <strong>{faDigits(kardex.data.totalIn)} عدد</strong>
                </div>
                <div>
                  مانده امانت کنونی:{" "}
                  <strong
                    style={{
                      color: kardex.data.overdueBalance > 0 ? "var(--coral-ink)" : undefined,
                      fontSize: 16,
                    }}
                  >
                    {faDigits(kardex.data.balance)} عدد
                  </strong>
                </div>
                <div>
                  معوقه:{" "}
                  {kardex.data.overdueBalance > 0 ? (
                    <Chip tone="coral">{faDigits(kardex.data.overdueBalance)} عدد</Chip>
                  ) : (
                    <Chip tone="ok">ندارد</Chip>
                  )}
                </div>
              </div>
            </div>

            <div className="card" style={{ padding: "var(--s2) var(--s3)" }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>تاریخ و ساعت</th>
                    <th>نوع عملیات</th>
                    <th>تعداد</th>
                    <th>توضیحات / علت</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {kardex.data.entries.map((entry) => {
                    const k = DIRECTION_LABELS[entry.direction] ?? { label: entry.direction };
                    const isReversal = entry.direction === "reversal" || Boolean(entry.reversalOfEntryId);

                    return (
                      <tr
                        key={entry.id}
                        style={{
                          opacity: isReversal ? 0.7 : undefined,
                        }}
                      >
                        <td className="muted" style={{ fontSize: 13 }}>
                          {entry.businessDate} {new Date(entry.instant).toLocaleTimeString("fa-IR")}
                        </td>
                        <td>
                          <Chip tone={k.tone}>{k.label}</Chip>
                        </td>
                        <td
                          className="num b"
                          style={{
                            color: entry.direction === "out" ? "var(--warn-ink)" : "var(--ok-ink)",
                          }}
                        >
                          {entry.direction === "out"
                            ? `+${faDigits(entry.quantity)}`
                            : `-${faDigits(entry.quantity)}`}
                        </td>
                        <td>{entry.reason || "—"}</td>
                        <td className="end">
                          {entry.direction !== "reversal" && !entry.reversalOfEntryId && (
                            <Button
                              variant="ghost"
                              onClick={() => {
                                const reason = askReason(`سند مورخ ${entry.businessDate}`);
                                if (reason) reverse.run({ entryId: entry.id, reason });
                              }}
                              disabled={reverse.busy}
                            >
                              اصلاح سند
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ErrorLine error={reverse.error} />
          </>
        )}
      </div>
    </Panel>
  );
}

function ManualEntryModal({
  units,
  types,
  onClose,
}: {
  units: { id: string; name: string }[];
  types: ContainerTypeRow[];
  onClose: () => void;
}) {
  const [unitId, setUnitId] = useState(units[0]?.id ?? "");
  const [containerTypeId, setContainerTypeId] = useState(types[0]?.id ?? "");
  const [direction, setDirection] = useState<"out" | "in">("out");
  const [quantity, setQuantity] = useState("1");
  const [reason, setReason] = useState("");

  const submit = useAction(async () => {
    const q = Math.max(1, parseInt(quantity, 10) || 1);
    await distributionApi.recordContainerEntry({
      unitId,
      containerTypeId,
      businessDate: tehranToday(),
      direction,
      quantity: q,
      reason: reason.trim() ? reason.trim() : undefined,
    });
    onClose();
  }, [
    ["distribution", "containers", "balances"],
    ["distribution", "containers", "kardex"],
  ]);

  return (
    <Panel title="ثبت سند گردش دستی ظرف امانی">
      <form
        className="stack"
        style={{ gap: "var(--s3)" }}
        onSubmit={(e) => {
          e.preventDefault();
          submit.run(undefined);
        }}
      >
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--s3)" }}
        >
          <div className="field">
            <label htmlFor="manual-unit">واحد سازمانی</label>
            <select
              id="manual-unit"
              className="input"
              value={unitId}
              onChange={(e) => {
                setUnitId(e.target.value);
              }}
              required
            >
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="manual-type">نوع ظرف امانی</label>
            <select
              id="manual-type"
              className="input"
              value={containerTypeId}
              onChange={(e) => {
                setContainerTypeId(e.target.value);
              }}
              required
            >
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.code})
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="manual-direction">جهت گردش</label>
            <select
              id="manual-direction"
              className="input"
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value as "out" | "in");
              }}
              required
            >
              <option value="out">تحویل به واحد (خروج ظروف از آشپزخانه)</option>
              <option value="in">برگشت از واحد (ورود ظروف به آشپزخانه)</option>
            </select>
          </div>

          <Field
            id="manual-qty"
            label="تعداد (عدد)"
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => {
              setQuantity(e.target.value);
            }}
            required
          />
        </div>

        <Field
          id="manual-reason"
          label="توضیحات / علت ثبت دستی"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          placeholder="علت ثبت دستی..."
        />

        <ErrorLine error={submit.error} />

        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose} disabled={submit.busy}>
            انصراف
          </Button>
          <Button variant="primary" type="submit" disabled={submit.busy}>
            {submit.busy ? "در حال ثبت..." : "تأیید و ثبت سند"}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function ContainerTypesModal({ types, onClose }: { types: ContainerTypeRow[]; onClose: () => void }) {
  const [editingType, setEditingType] = useState<ContainerTypeRow | "new" | null>(null);

  return (
    <Panel
      title="تعریف انواع ظروف امانی"
      actions={
        <Button variant="ghost" onClick={onClose}>
          بستن
        </Button>
      }
    >
      <div className="stack" style={{ gap: "var(--s3)" }}>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => {
              setEditingType("new");
            }}
          >
            نوع ظرف جدید
          </Button>
        </div>

        {editingType && (
          <TypeEditor
            item={editingType === "new" ? null : editingType}
            onDone={() => {
              setEditingType(null);
            }}
          />
        )}

        <div className="card" style={{ padding: "var(--s2) var(--s3)" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>نام ظرف</th>
                <th>کد</th>
                <th>توضیحات</th>
                <th>وضعیت</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id}>
                  <td className="b">{t.name}</td>
                  <td className="num">{t.code}</td>
                  <td className="muted">{t.description || "—"}</td>
                  <td>{t.active ? <Chip tone="ok">فعال</Chip> : <Chip plain>غیرفعال</Chip>}</td>
                  <td className="end">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditingType(t);
                      }}
                    >
                      ویرایش
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

function TypeEditor({ item, onDone }: { item: ContainerTypeRow | null; onDone: () => void }) {
  const [name, setName] = useState(item?.name ?? "");
  const [code, setCode] = useState(item?.code ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [active, setActive] = useState(item?.active ?? true);

  const save = useAction(async () => {
    if (!item) {
      await distributionApi.createContainerType({
        name: name.trim(),
        code: code.trim(),
        description: description.trim() ? description.trim() : undefined,
        active,
      });
    } else {
      await distributionApi.updateContainerType(item.id, item.version, {
        name: name.trim(),
        description: description.trim() ? description.trim() : undefined,
        active,
      });
    }
    onDone();
  }, [["distribution", "containers", "types"]]);

  return (
    <div className="card" style={{ padding: "var(--s3)", background: "var(--panel)" }}>
      <form
        className="stack"
        style={{ gap: "var(--s3)" }}
        onSubmit={(e) => {
          e.preventDefault();
          save.run(undefined);
        }}
      >
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--s3)" }}
        >
          <Field
            id="type-name"
            label="نام ظرف"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="مثلاً: بن‌ماری استیل بزرگ"
            required
          />
          <Field
            id="type-code"
            label="کد یکتا"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            placeholder="مثلاً: BM-LG"
            disabled={Boolean(item)}
            required
          />
          <Field
            id="type-desc"
            label="توضیحات"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            placeholder="مشخصات ظرف..."
          />
        </div>

        <Switch label="فعال" checked={active} onChange={setActive} />

        <ErrorLine error={save.error} />

        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onDone} disabled={save.busy}>
            انصراف
          </Button>
          <Button variant="primary" type="submit" disabled={save.busy || !name.trim()}>
            {save.busy ? "در حال ثبت..." : "ذخیره نوع ظرف"}
          </Button>
        </div>
      </form>
    </div>
  );
}
